import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { API } from '../config/api';

/**
 * Reusable inline "Create new client" dialog.
 * onCreated(client) is called with the newly created client (already saved server-side).
 * `defaults` allows pre-filling fields (e.g. when AI extracted partial data).
 */
export default function NewClientDialog({ open, onOpenChange, onCreated, defaults }) {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', address: '', cantiere: '', notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: defaults?.name || '',
        phone: defaults?.phone || '',
        email: defaults?.email || '',
        address: defaults?.address || '',
        cantiere: defaults?.cantiere || '',
        notes: defaults?.notes || '',
      });
    }
  }, [open, defaults]);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) {
      toast.error('Il nome del cliente è obbligatorio');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        cantiere: form.cantiere.trim(),
        notes: form.notes.trim(),
        status: 'lead',
        pipeline_stage: 'new_lead',
      };
      const { data } = await axios.post(`${API}/clients`, payload);
      toast.success('Cliente creato');
      if (onCreated) onCreated(data);
      if (onOpenChange) onOpenChange(false);
    } catch (err) {
      console.error('Create client failed:', err);
      toast.error(err.response?.data?.detail || 'Errore nel creare il cliente');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit">Nuovo cliente</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-3">
          <div>
            <Label htmlFor="ncd-name" className="text-xs">Nome / Ragione sociale *</Label>
            <Input
              id="ncd-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Mario Rossi / Rossi Srl"
              autoFocus
              data-testid="new-client-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="ncd-phone" className="text-xs">Telefono</Label>
              <Input
                id="ncd-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+39..."
                inputMode="tel"
                data-testid="new-client-phone"
              />
            </div>
            <div>
              <Label htmlFor="ncd-email" className="text-xs">Email</Label>
              <Input
                id="ncd-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="cliente@esempio.it"
                data-testid="new-client-email"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="ncd-address" className="text-xs">Indirizzo (residenza)</Label>
            <Input
              id="ncd-address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Via, civico, città"
              data-testid="new-client-address"
            />
          </div>
          <div>
            <Label htmlFor="ncd-cantiere" className="text-xs">
              Cantiere <span className="text-[10px] text-[#A0AEC0]">(luogo di installazione, opzionale)</span>
            </Label>
            <Input
              id="ncd-cantiere"
              value={form.cantiere}
              onChange={(e) => setForm({ ...form, cantiere: e.target.value })}
              placeholder="Indirizzo del cantiere se diverso"
              data-testid="new-client-cantiere"
            />
          </div>
          <div>
            <Label htmlFor="ncd-notes" className="text-xs">Note</Label>
            <Textarea
              id="ncd-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              placeholder="Note interne sul cliente..."
              data-testid="new-client-notes"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={saving}
              data-testid="new-client-cancel"
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
              data-testid="new-client-save"
            >
              {saving ? 'Salvataggio...' : 'Crea cliente'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
